import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  Info, RefreshCw, Ban, Copy, Check, KeyRound, X,
} from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import useApiKeysApi, {
  API_KEY_NAME_MAX, availablePermissions, buildPermissionAsync, apiKeyStatus,
  unheldPermissions, permissionLabel, rotationLimitBlock, hasEventsOnlyPermission, writeModulesLabel,
} from './config';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import TextInput from '../../components/ui/TextInput.jsx';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import IconButton from '../../components/ui/IconButton';
import Tooltip from '../../components/ui/Tooltip';
import Button from '../../components/ui/Button';
import LoadErrorBanner from '../../components/ui/LoadErrorBanner';

// Entegrasyonlar > API Anahtarları (Public API Plan 01, Görev 8; tasarım belgesi §4.6).
//
// Liste EmployeesPage aynasıdır: başlık ve "Yeni" düğmesi DataTable'ın İÇİNDE. Oluşturma
// GenericFormDrawer ile (modal DEĞİL), KABUK deseninde (schema=[] + children —
// GoalTemplatesTab emsali): ad alanı sunucunun 200 karakter sınırıyla kesilir ve boş izin
// listesi istemcide yakalanır; GenericFormDrawer'ın şema alanları ikisini de taşımıyor.
//
// TAM ANAHTAR BİR KEZ: oluşturma/yenileme yanıtındaki `plainKey` yalnız tablonun üstündeki
// "Yeni Anahtarınız" panelinde yaşar; panel kapanınca state sıfırlanır ve bir daha gösterilmez —
// liste yalnız maskeli anahtarı (liste ucundan) bilir. Panel bir çekmece DEĞİL: çekmecenin
// sabit "İptal" düğmesi bu ekranda anahtarı "İptal Et" eylemiyle karışırdı (kullanıcı anahtarı
// iptal ettiğini sanıp aktif bırakabilirdi).
const Container = styled.div`
  display: flex;
  flex-direction: column;
  /* İç içe scroll yasak — tek kaydırma DataTable satırlarıdır. */
  min-height: 0;
`;

const Notice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 12px;
  padding: 10px 14px;
  border-radius: 6px;
  border-left: 3px solid ${({ theme }) => theme.colors.primary.main};
  background: #EEF6FD;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  line-height: 1.4;

  svg { flex-shrink: 0; margin-top: 1px; color: ${({ theme }) => theme.colors.primary.main}; }
`;

const NoticeBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const EmptyLine = styled.span`
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
`;

const RevealPanel = styled.div`
  margin-bottom: 12px;
  padding: 12px 14px;
  border-radius: 6px;
  border-left: 3px solid #FFC107;
  background: #FFF8E6;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  line-height: 1.4;
`;

const RevealHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;

  > svg { flex-shrink: 0; color: #B7791F; }
`;

const RevealTitle = styled.div`
  flex: 1;
  min-width: 0;
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
`;

const RevealText = styled.p`
  margin: 0 0 4px;
`;

const KeyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
  flex-wrap: wrap;
`;

const PlainKey = styled.div`
  flex: 1;
  min-width: 0;
  font-family: monospace;
  font-size: 13px;
  background: #fff;
  border: 1px dashed #C5CCD6;
  border-radius: 6px;
  padding: 8px 12px;
  word-break: break-all;
  user-select: all;
`;

const FieldGroup = styled.div`
  margin-bottom: 16px;
`;

// Alanın altındaki açıklama: POZİTİF üst marj (UI altın kuralı 4).
const Hint = styled.p`
  font-size: 12px;
  color: #6c757d;
  margin: 6px 0 0;
`;

const RowActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 6px;
`;

// Onay diyaloğunda HANGİ anahtarın etkileneceği (ad · maske) — çeviriye gömülmez.
const TargetRef = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};

  span { font-family: monospace; }
`;

export default function ApiKeysTab() {
  const { t } = useTranslation();
  const { canWrite, canRead, hasModule } = useAuth();
  const canManage = Boolean(canWrite('PA'));

  const {
    fetching, failed, data, limits, columns: baseColumns, create, rotate, revoke,
  } = useApiKeysApi();

  // Oluşturma çekmecesinin taslağı (KABUK deseni).
  const [formOpen, setFormOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [permsDraft, setPermsDraft] = useState([]);
  const [nameError, setNameError] = useState('');
  const [permsError, setPermsError] = useState('');

  // { name, plainKey, rotated } — YALNIZ panel açıkken; kapanınca null.
  const [revealed, setRevealed] = useState(null);
  const [copied, setCopied] = useState(false);

  const [rotateTarget, setRotateTarget] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  // Seçenekler: kiracıda aktif VE kullanıcının kendisinin okuyabildiği modüller.
  const permissionOptions = useMemo(() => availablePermissions(hasModule, canRead, canWrite), [hasModule, canRead, canWrite]);
  const permissionAsync = useMemo(() => buildPermissionAsync(t, permissionOptions), [t, permissionOptions]);
  const showEventsOnlyHint = hasEventsOnlyPermission(permissionOptions);

  const openForm = useCallback(() => {
    setNameDraft('');
    setPermsDraft([]);
    setNameError('');
    setPermsError('');
    setFormOpen(true);
  }, []);

  const closeForm = useCallback(() => setFormOpen(false), []);

  const reveal = useCallback((created, rotated) => {
    setCopied(false);
    setRevealed({ name: created.name, plainKey: created.plainKey, rotated });
  }, []);

  const closeReveal = useCallback(() => {
    setRevealed(null);
    setCopied(false);
  }, []);

  // false dönerse çekmece açık kalır (geçersiz form kapısı — düğme kilitlemek değil).
  const handleCreate = useCallback(async () => {
    const name = nameDraft.trim();
    const nextNameError = name ? '' : t('responseErrors.apiKey.nameRequired');
    const nextPermsError = permsDraft.length > 0 ? '' : t('responseErrors.apiKey.permissionsRequired');
    setNameError(nextNameError);
    setPermsError(nextPermsError);
    if (nextNameError || nextPermsError) return false;

    const created = await create({ name, permissions: permsDraft });
    if (!created) return false;   // hata toast'ı interceptor'da; çekmece açık kalır
    reveal(created, false);
    return true;
  }, [nameDraft, permsDraft, create, reveal, t]);

  const handleRotateConfirm = useCallback(async () => {
    if (!rotateTarget) return;
    setBusy(true);
    const created = await rotate(rotateTarget.id);
    setBusy(false);
    setRotateTarget(null);
    if (created) reveal(created, true);
  }, [rotateTarget, rotate, reveal]);

  const handleRevokeConfirm = useCallback(async () => {
    if (!revokeTarget) return;
    setBusy(true);
    await revoke(revokeTarget.id);
    setBusy(false);
    setRevokeTarget(null);
  }, [revokeTarget, revoke]);

  const handleCopy = useCallback(async () => {
    if (!revealed) return;
    try {
      await navigator.clipboard.writeText(revealed.plainKey);
      setCopied(true);
    } catch {
      // Pano erişimi yoksa kullanıcı anahtarı elle seçer (kutu tek tıkla tümünü seçer).
    }
  }, [revealed]);

  // Satır eylemleri yalnız PA.Write'ta ve yalnız AKTİF anahtarda: iptal edilmiş ya da süresi
  // dolmuş anahtarda ne yenileme ne iptal anlamlıdır.
  const columns = useMemo(() => {
    if (!canManage) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false,
          sort: false,
          download: false,
          print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            if (!row || apiKeyStatus(row) !== 'active') return null;
            // Eylem gizlenmez, devre dışı kalır ve nedeni tooltip'te söylenir. İki kapı:
            //  1) YETKİ: rotasyon eski anahtarın izinlerini yeni anahtara taşır; kullanıcının
            //     okuyamadığı modülü kapsayan anahtarı yenilemek yetki yükseltmesi olurdu
            //     (sunucu 403 apiKey.permissionNotHeld). Kalıcı neden olduğu için ÖNCE söylenir.
            //  2) SINIR (rotationLimitBlock): süresiz anahtarın yenilenmesi bir geçiş yuvası,
            //     geçişteki anahtarın yenilenmesi bir süresiz yuvası ister (sunucu 400
            //     apiKey.rotationLimitExceeded / apiKey.limitExceeded). Süresiz anahtarları
            //     sınırda olan kiracı yenilemeyi YAPABİLİR — kapı yalnız dolan sayaca bakar.
            const unheld = unheldPermissions(row.permissions, canRead, canWrite);
            const limitBlock = rotationLimitBlock(row, limits);
            const rotateBlocked = unheld.length > 0 || limitBlock !== null;
            let rotateTip = t('apiKeys.rotate');
            if (unheld.length > 0) {
              rotateTip = t('apiKeys.rotateNotAllowed', { modules: unheld.map((p) => permissionLabel(t, p)).join(', ') });
            } else if (limitBlock === 'transition') {
              rotateTip = t('apiKeys.rotateLimitTransition', { max: limits.maxApiKeys });
            } else if (limitBlock === 'primary') {
              rotateTip = t('apiKeys.rotateLimitPrimary', { max: limits.maxApiKeys });
            }
            return (
              <RowActions>
                <Tooltip text={rotateTip}>
                  <IconButton
                    type="button"
                    size="sm"
                    data-role={`api-key-rotate-${row.id}`}
                    aria-label={rotateTip}
                    disabled={rotateBlocked}
                    // Devre dışı <button> fare olayı üretmez; olay sarmalayıcıya (Tooltip)
                    // düşsün ki neden görünsün.
                    style={rotateBlocked ? { pointerEvents: 'none' } : undefined}
                    onClick={() => { if (!rotateBlocked) setRotateTarget(row); }}
                  >
                    <RefreshCw size={16} />
                  </IconButton>
                </Tooltip>
                <Tooltip text={t('apiKeys.revoke')}>
                  <IconButton
                    type="button"
                    size="sm"
                    variant="danger"
                    data-role={`api-key-revoke-${row.id}`}
                    aria-label={t('apiKeys.revoke')}
                    onClick={() => setRevokeTarget(row)}
                  >
                    <Ban size={16} />
                  </IconButton>
                </Tooltip>
              </RowActions>
            );
          },
        },
      },
    ];
  }, [canManage, baseColumns, data, t, canRead, canWrite, limits]);

  const targetRef = (row) => (row ? (
    <TargetRef>
      {row.name}
      {' · '}
      <span>{row.maskedKey}</span>
    </TargetRef>
  ) : null);

  // "Yüklenemedi" ile "hiç anahtar yok" AYRI: yükleme başarısızsa boş-durum metni söylenmez.
  const isEmpty = !fetching && !failed && data.length === 0;

  return (
    <Container>
      {canManage && (
        <GenericFormDrawer
          open={formOpen}
          onClose={closeForm}
          title={t('apiKeys.new')}
          schema={[]}
          onSave={handleCreate}
        >
          <FieldGroup>
            <TextInput
              label={t('apiKeys.name')}
              value={nameDraft}
              onChange={(e) => { setNameDraft(e.target.value); setNameError(''); }}
              maxLength={API_KEY_NAME_MAX}
              required
              error={nameError || undefined}
              data-role="api-key-name"
            />
          </FieldGroup>
          <FieldGroup>
            <AsyncGenericSelect
              label={t('apiKeys.permissions')}
              value={permsDraft}
              onChange={(v) => { setPermsDraft(Array.isArray(v) ? v : []); setPermsError(''); }}
              onSearch={permissionAsync.onSearch}
              onGetById={permissionAsync.onGetById}
              isMulti
              required
              error={permsError || undefined}
            />
            <Hint>{t('apiKeys.permissionsNotice', { modules: writeModulesLabel(t) })}</Hint>
            {showEventsOnlyHint && <Hint data-role="api-keys-events-only">{t('apiKeys.eventsOnlyNotice')}</Hint>}
          </FieldGroup>
        </GenericFormDrawer>
      )}

      <ConfirmDialog
        isOpen={!!rotateTarget}
        type="info"
        title={t('apiKeys.rotateTitle')}
        message={t('apiKeys.rotateInfo')}
        confirmText={t('apiKeys.rotate')}
        cancelText={t('apiKeys.dismiss')}
        onConfirm={handleRotateConfirm}
        onCancel={() => setRotateTarget(null)}
        loading={busy}
      >
        {targetRef(rotateTarget)}
      </ConfirmDialog>

      <ConfirmDialog
        isOpen={!!revokeTarget}
        title={t('apiKeys.revokeTitle')}
        message={t('apiKeys.revokeConfirm')}
        confirmText={t('apiKeys.revoke')}
        cancelText={t('apiKeys.dismiss')}
        onConfirm={handleRevokeConfirm}
        onCancel={() => setRevokeTarget(null)}
        loading={busy}
      >
        {targetRef(revokeTarget)}
      </ConfirmDialog>

      {failed && <LoadErrorBanner message={t('apiKeys.loadFailed')} />}

      {revealed && (
        <RevealPanel data-role="api-key-reveal" role="status">
          <RevealHeader>
            <KeyRound size={16} />
            <RevealTitle>
              {t('apiKeys.createdTitle')}
              {': '}
              {revealed.name}
            </RevealTitle>
            <Tooltip text={t('common.close')}>
              <IconButton
                type="button"
                size="sm"
                data-role="close-plain-key"
                aria-label={t('common.close')}
                onClick={closeReveal}
              >
                <X size={16} />
              </IconButton>
            </Tooltip>
          </RevealHeader>
          <RevealText data-role="created-once">{t('apiKeys.createdOnce')}</RevealText>
          {revealed.rotated && <RevealText data-role="rotate-info">{t('apiKeys.rotateInfo')}</RevealText>}
          <KeyRow>
            <PlainKey data-role="plain-key">{revealed.plainKey}</PlainKey>
            <Button variant="outline" type="button" data-role="copy-plain-key" onClick={handleCopy}>
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? t('apiKeys.copied') : t('apiKeys.copy')}
            </Button>
          </KeyRow>
        </RevealPanel>
      )}

      <Notice data-role="api-keys-notice">
        <Info size={16} />
        <NoticeBody>
          {isEmpty && <EmptyLine data-role="api-keys-empty">{t('apiKeys.empty')}</EmptyLine>}
          <span data-role="api-keys-permissions-notice">{t('apiKeys.permissionsNotice', { modules: writeModulesLabel(t) })}</span>
        </NoticeBody>
      </Notice>

      <DataTable
        title={t('apiKeys.title')}
        fetching={fetching}
        columns={columns}
        data={data}
        serverSide={false}
        disableFilters
        canCreateNewRecord={canManage}
        newButtonLabel={t('apiKeys.new')}
        onNewButtonClick={openForm}
        tableBodyMaxHeight="calc(100vh - 340px)"
      />
    </Container>
  );
}
